import React from 'react';
import { pageHeroPhotos } from '../data/pageHeroPhotos';
import { CoffeeIcon, CreditCardIcon, GraduationCapIcon, ShieldCheckIcon, TvIcon, WifiIcon, WrenchIcon } from 'lucide-react';
import { PageHeading } from '../components/PageHeading';
import { AboutVisit } from '../components/AboutVisit';
import { CallBand } from '../components/CallBand';
import { fourCs, photos } from '../data/site';
import { usePageMeta } from '../hooks/usePageMeta';
const trustItems = [{
  icon: WrenchIcon,
  title: '55+ years of combined experience',
  copy: 'Our technicians bring decades of hands-on repair experience to every vehicle that comes in.'
}, {
  icon: GraduationCapIcon,
  title: 'FCA factory trained',
  copy: 'Factory training from FCA Chrysler, Dodge and Jeep backs the way we diagnose and repair.'
}, {
  icon: WrenchIcon,
  title: 'Modern diagnostic equipment',
  copy: 'Up-to-date, cutting-edge equipment so testing produces answers instead of guesses.'
}, {
  icon: ShieldCheckIcon,
  title: '1 year / 12,000 mile warranty',
  copy: 'Parts and labor covered on the repairs we perform.'
}, {
  icon: CreditCardIcon,
  title: 'Financing options available',
  copy: 'Snap Finance, Affirm, Klarna and Sunbit. Ask us when a repair is larger than expected.'
}, {
  icon: WifiIcon,
  title: 'Free guest Wi-Fi',
  copy: 'Work or stream while you wait in the customer lounge.'
}, {
  icon: TvIcon,
  title: 'TV & coffee bar',
  copy: 'A comfortable waiting room with TV and coffee while we work.'
}, {
  icon: CoffeeIcon,
  title: 'Straight answers, every visit',
  copy: 'You will always know what we found, what it costs, and what can wait.'
}];
export function TheEastEndWay() {
  usePageMeta('The East End Automotive Way | Auto Repair in Oak Ridge, TN', 'How East End Automotive Repair in Oak Ridge, TN works: honest diagnosis, the 4 C’s process, and clear explanations before any repair. Call (865) 206-2279.');
  return <>
      <PageHeading photo={{
      ...pageHeroPhotos.eastEndWay,
      position: 'center 38%',
      mobilePosition: '35% 40%'
    }} eyebrow="Our approach" title="Auto Repair Built on Honest Diagnosis" intro="The East End Automotive Way is simple: test before assuming, find the actual cause, explain the options in plain language, and let you decide what needs attention now and what can wait." data-id="element-935" data-mp="dtnizq73bb7s" />

      <AboutVisit data-id="element-936" data-mp="wkyb2aojxu19" />

      <section className="w-full bg-white py-16 lg:py-24" data-id="element-937" data-mp="rk0rbtnw8rxn">
        <div className="mx-auto max-w-shell px-5 lg:px-8" data-id="element-938" data-mp="rkiw5eomgeh6">
          <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,440px)] lg:items-start lg:gap-16" data-id="element-939" data-mp="ms8z7ep3ae5e">
            <div data-id="element-940" data-mp="iqe08gumscus">
              <h2 className="max-w-[560px] font-display text-[30px] font-extrabold leading-[1.1] tracking-[-0.02em] text-ink sm:text-[36px]" data-id="element-941" data-mp="iqxg7s0q0r66">
                Communication first, repairs second
              </h2>
              <p className="mt-5 max-w-[620px] text-[17px] leading-relaxed text-steel/80" data-id="element-942" data-mp="1ahbmp36en17t">
                Anyone can replace a part. Our work starts with an honest,
                professional opinion about what your vehicle actually needs,
                explained clearly enough that you can weigh the options
                yourself. We would rather educate you and recommend the right
                solution than sell you a repair you did not need.
              </p>

              <h3 className="mt-12 text-[12px] font-bold uppercase tracking-[0.18em] text-brand-dark" data-id="element-943" data-mp="3c3frfsp3nyqc">
                The 4 C’s process
              </h3>
              <ol className="mt-5 border-t border-black/10" data-id="element-944" data-mp="3luafn2lwp8ef">
                {fourCs.map((step, i) => <li key={step.label} className="grid grid-cols-[auto_minmax(0,1fr)] gap-5 border-b border-black/10 py-6 sm:gap-7" data-id="element-945" data-mp="3pw5em0gd79p1" data-mpi={i}>
                    <span aria-hidden="true" className="mt-1 flex h-9 w-9 items-center justify-center rounded-md bg-ink font-display text-sm font-bold text-brand-light" data-id="element-946" data-mp="3o5qwp0uz1ko">
                      {i + 1}
                    </span>
                    <div data-id="element-947" data-mp="3vww5kvcjmr0y">
                      <h4 className="font-display text-xl font-bold tracking-tight text-ink" data-id="element-948" data-mp="3vx5pc16zw1y7">
                        {step.label}
                      </h4>
                      <p className="mt-1.5 max-w-[560px] text-[15px] leading-relaxed text-steel/75" data-id="element-949" data-mp="3r05k1shyz385">
                        {step.copy}
                      </p>
                    </div>
                  </li>)}
              </ol>
            </div>

            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-1" data-id="element-950" data-mp="3d7zdmy4mcalw">
              <figure data-id="element-951" data-mp="3871vumnitikx">
                <img src={photos.diagnosticMercedes} alt="Vehicle connected to diagnostic equipment in the service bay" className="aspect-[4/3] w-full rounded-lg object-cover object-[center_28%]" data-id="element-952" data-mp="386iasx2kqe2o" />
                <figcaption className="mt-2.5 text-[13px] text-steel/60" data-id="element-953" data-mp="v027aitxt11o">
                  Testing until the cause is confirmed, not assumed.
                </figcaption>
              </figure>
              <figure data-id="element-954" data-mp="m0f720cy21a7">
                <img src={photos.consultation} alt="Technician explaining a confirmed diagnosis and repair options to a customer" className="aspect-[4/3] w-full rounded-lg object-cover" data-id="element-955" data-mp="m0ynfcmy4hm4" />
                <figcaption className="mt-2.5 text-[13px] leading-snug text-steel/60" data-id="element-956" data-mp="1iq6awq74l0so">
                  Once we confirm the diagnosis, we explain the issue and your
                  repair options clearly.
                </figcaption>
              </figure>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-chalk py-16 lg:py-24" data-id="element-957" data-mp="1wpfkh70tswno">
        <div className="mx-auto max-w-shell px-5 lg:px-8" data-id="element-958" data-mp="1wnupxheihdpt">
          <h2 className="max-w-[560px] font-display text-[30px] font-extrabold leading-[1.1] tracking-[-0.02em] text-ink sm:text-[36px]" data-id="element-959" data-mp="21hh1t7cupdkk">
            What stands behind the work
          </h2>
          <ul className="mt-10 grid gap-x-10 gap-y-8 sm:grid-cols-2 lg:grid-cols-4" data-id="element-960" data-mp="27c437hkiamju">
            {trustItems.map(item => <li key={item.title} className="flex flex-col" data-id="element-961" data-mp="22a4xw257hfk9">
                <item.icon className="h-6 w-6 text-brand-dark" aria-hidden="true" data-id="element-962" data-mp="1xdo873icyt52" />
                <h3 className="mt-3 font-display text-[16px] font-bold leading-snug tracking-tight text-ink" data-id="element-963" data-mp="1sf39j8ir8hiq">
                  {item.title}
                </h3>
                <p className="mt-2 text-[14px] leading-relaxed text-steel/70" data-id="element-964" data-mp="1jkoxrfxfnp4d">
                  {item.copy}
                </p>
              </li>)}
          </ul>
        </div>
      </section>

      <CallBand heading="Give us a call today to learn more about the East End Automotive Way!" copy="Talk it through with the shop and we will tell you what your vehicle likely needs and when we can get it in." secondary={{
      label: 'View Services',
      to: '/services'
    }} data-id="element-965" data-mp="19m91yzngnroj" />
    </>;
}